import{ FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Form, Input } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, Link } from 'react-router-dom';
import type { Location } from 'history';

import { useFelaEnhanced } from 'hooks';
import { Logo, useUIMessage } from 'modules/ui';
import config from 'config/index';

import { forms, fields } from '../../config';
import type { LoginFormValues } from '../../types';
import AuthForm, { SubmitAuthFormButton } from '../AuthForm';
import * as rules from './LoginForm.styles';

export interface LoginFormProps {}

interface LoginLocationState {
    passwordConfirmed?: boolean;
    forceChangePassword?: boolean;
}

const LoginForm: FC<LoginFormProps> = () => {
    const { styles } = useFelaEnhanced(rules);
    const { formatMessage } = useIntl();
    const location: Location<LoginLocationState> = useLocation();
    const message = useUIMessage();
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const endpoint = useMemo(() => `${config.api.base}${config.api.salesforceMagicLoginRequest}`, []);

    useEffect(() => {
        if (location.state?.passwordConfirmed) {
            message('info', 'auth.passwordWasChanged');
        }
        if (location.state?.forceChangePassword) {
            message('info', 'auth.forgotPassword.forceChangePassword');
        }
    }, [location.state, message]);

    const handleSubmit = useCallback(async ({ email }: LoginFormValues) => {
        setSubmitting(true);
        setError(null);
        setSent(false);
        const redirectUrl = new URLSearchParams(location.search).get('redirectUrl');
        try {
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email,
                    ...(redirectUrl ? { redirectUrl } : {}),
                }),
            });
            const data = await res.json().catch(() => null);

            if (!res.ok || !data?.success) {
                setError('auth.magicLink.requestError');
                return;
            }

            setSent(true);
        } catch {
            setError('auth.magicLink.requestError');
        } finally {
            setSubmitting(false);
        }
    }, [endpoint, location.search]);

    return (
        <>
            <AuthForm name={forms.login} layout="vertical" className={styles.form} onFinish={handleSubmit}>
                <Logo className={styles.logo} />

                <Form.Item
                    name={fields.login.email}
                    label={formatMessage({ id: 'auth.email' })}
                    rules={[{ required: true, message: formatMessage({ id: 'form.validation.required.email' }) }]}
                >
                    <Input
                        type="email"
                        size="large"
                        autoComplete="email"
                        placeholder={formatMessage({ id: 'auth.email' })}
                    />
                </Form.Item>

                {sent && (
                    <Alert
                        type="success"
                        showIcon
                        message={formatMessage({ id: 'auth.magicLink.sent' })}
                        style={{ marginBottom: 16 }}
                    />
                )}

                {error && (
                    <Alert
                        type="error"
                        showIcon
                        message={formatMessage({ id: error })}
                        style={{ marginBottom: 16 }}
                    />
                )}

                {/*
                  Old email + password form (kept for easy rollback):

                  <Form.Item
                      name={fields.login.password}
                      label={formatMessage({ id: 'auth.password' })}
                      rules={[{ required: true, message: formatMessage({ id: 'form.validation.required.password' }) }]}
                  >
                      <Input
                          type="password"
                          size="large"
                          autoComplete="current-password"
                          placeholder={formatMessage({ id: 'auth.password' })}
                      />
                  </Form.Item>
                */}

                <Form.Item>
                    <SubmitAuthFormButton
                        type="primary"
                        htmlType="submit"
                        size="large"
                        disabled={submitting}
                        loading={submitting}
                    >
                        {formatMessage({ id: 'auth.magicLink.sendButton' })}
                    </SubmitAuthFormButton>
                </Form.Item>
                <div className={styles.link}>
                    <Link to={config.routes.registration}>
                        <FormattedMessage id="auth.login.registration" />
                    </Link>
                </div>
                {/*
                  Password reset link (uncomment if reverting to password login):
                  <div className={styles.link}>
                      <Link to={generatePath(config.routes.forgotPassword, { step: ForgotPasswordSteps.EMAIL })}>
                          <FormattedMessage id="auth.login.forgottenPassword" />
                      </Link>
                  </div>
                */}
                <div className={styles.link}>
                    <Link to={config.routes.guestLogin}>
                        <FormattedMessage id="auth.login.guestAccess" />
                    </Link>
                </div>
            </AuthForm>
        </>
    );
};

export default LoginForm;
